/**
 * Trust / legal copy for launch — single place to edit wording.
 * Keep factual and parent-friendly; not formal legal advice.
 */

import { BRANDING } from "../config/branding";

export const TRUST_CONTACT_EMAIL = "hello@apemind.app";

export const TRUST_COVERAGE = {
  shortLabel: "England",
  summary:
    "We are building coverage across England, starting with English grammar schools. Catchment and open-admissions detail may still be incomplete in some areas — always check with the school and local authority.",
};

export const TRUST_DISCLAIMER_SHORT =
  "Guidance only — not official admissions advice. Always check with the school and local authority.";

export const TRUST_ATTRIBUTION = {
  short:
    "School performance and inspection data from public sources including the Department for Education and Ofsted. Maps © OpenStreetMap contributors. Travel times use Google Directions where shown.",
  items: [
    {
      label: "Department for Education",
      href: "https://www.gov.uk/government/organisations/department-for-education",
    },
    {
      label: "Ofsted",
      href: "https://www.gov.uk/government/organisations/ofsted",
    },
    {
      label: "OpenStreetMap",
      href: "https://www.openstreetmap.org/copyright",
    },
    {
      label: "Google Directions",
      href: "https://developers.google.com/maps/documentation/directions",
    },
  ],
};

/** @typedef {'privacy' | 'terms' | 'about' | 'contact'} TrustPageId */

/** @type {Record<TrustPageId, { id: TrustPageId, title: string, sections: { heading?: string, paragraphs: string[] }[] }>} */
export const TRUST_PAGES = {
  about: {
    id: "about",
    title: `About ${BRANDING.appName}`,
    sections: [
      {
        paragraphs: [
          `${BRANDING.appName} helps parents explore schools on a map — catchments, open admissions patterns, nearby options, Ofsted-style pin cues, results, and travel from home.`,
        ],
      },
      {
        heading: "What it is for",
        paragraphs: [
          "Compare options around a home postcode, see which schools look in catchment or open to applicants more widely, and open school details in one place.",
        ],
      },
      {
        heading: "What it is not",
        paragraphs: [
          "It is not an official admissions service, a guarantee of a place, or a substitute for local authority or school guidance.",
          TRUST_COVERAGE.summary,
        ],
      },
    ],
  },
  privacy: {
    id: "privacy",
    title: "Privacy",
    sections: [
      {
        paragraphs: [
          `This privacy notice explains how ${BRANDING.appName} handles information when you use the website.`,
        ],
      },
      {
        heading: "What we process",
        paragraphs: [
          "Postcodes or map locations you enter to place a home marker and check nearby schools.",
          "Technical data such as browser type and basic request logs needed to run and secure the service.",
          "If you email us, the content of that message and your email address.",
        ],
      },
      {
        heading: "What we do not do",
        paragraphs: [
          "We do not require an account to explore the map.",
          "We do not sell your personal information.",
        ],
      },
      {
        heading: "Maps and travel",
        paragraphs: [
          "Map tiles and, where used, travel directions are provided by third-party services. Those providers may process technical data under their own policies.",
        ],
      },
      {
        heading: "Contact",
        paragraphs: [
          `Questions about privacy: ${TRUST_CONTACT_EMAIL}`,
        ],
      },
    ],
  },
  terms: {
    id: "terms",
    title: "Terms of use",
    sections: [
      {
        paragraphs: [
          `By using ${BRANDING.appName} you agree to these terms.`,
        ],
      },
      {
        heading: "Informational use",
        paragraphs: [
          "Content is for general information and parental research only. Admissions rules change. Always confirm with the school and the relevant local authority before applying.",
        ],
      },
      {
        heading: "Accuracy",
        paragraphs: [
          "We aim to keep school and catchment information useful and up to date, but we do not warrant completeness or accuracy. Catchment and open-admissions labels are based on available data and may be incomplete.",
        ],
      },
      {
        heading: "Acceptable use",
        paragraphs: [
          "Do not misuse the service (for example attempting to disrupt it, scrape aggressively, or use it for unlawful purposes).",
        ],
      },
      {
        heading: "Liability",
        paragraphs: [
          `${BRANDING.appName} is provided as-is. To the fullest extent permitted by law, we are not liable for decisions made using the site, including school applications or travel choices.`,
        ],
      },
      {
        heading: "Contact",
        paragraphs: [
          `Questions about these terms: ${TRUST_CONTACT_EMAIL}`,
        ],
      },
    ],
  },
  contact: {
    id: "contact",
    title: "Contact",
    sections: [
      {
        paragraphs: [
          "Spot wrong school data, a catchment issue, or have feedback? We want to hear from you.",
          `Email: ${TRUST_CONTACT_EMAIL}`,
        ],
      },
      {
        heading: "Please include",
        paragraphs: [
          "The school name (and URN/code if you know it), your postcode area, and what looks wrong. That helps us fix issues faster.",
        ],
      },
    ],
  },
};

export const TRUST_FOOTER_LINKS = [
  { id: "about", label: "About" },
  { id: "privacy", label: "Privacy" },
  { id: "terms", label: "Terms" },
  { id: "contact", label: "Contact" },
];
